import { z } from 'zod';
/**
 * App 2 contracts (spec §10), shared by the field form and the route.
 *
 * Two things here are shaped by the app being field-first rather than
 * desk-bound, and both would look odd in an online-only design.
 */
export const issueCategorySchema = z.enum(['minor', 'moderate', 'major']);
export const issueImpactSchema = z.enum(['positive', 'negative']);
export const photoCategorySchema = z.enum(['before', 'after']);
/**
 * Whether a location fix was obtained, and if not, why.
 *
 * Spec §10 requires GPS to be system-captured, not typed, but says nothing about
 * a device that cannot get a fix. Recording the reason keeps the distinction
 * between "nobody tried" and "there was no signal in the basement" — and lets
 * the form submit either way, because a lesson is worth more than its
 * coordinates.
 */
export const gpsCaptureStatusSchema = z.enum(['captured', 'denied', 'unavailable', 'timed_out']);
const narrative = z.string().trim().max(10000);
export const lessonCreateSchema = z
    .object({
    /**
     * Generated on the DEVICE, not the server. A form created with no signal
     * keeps its identity through sync, which is what lets a photo queued beside
     * it still know which form it belongs to.
     */
    id: z.string().uuid().optional(),
    projectId: z.string().uuid(),
    title: z.string().trim().min(1, 'Give the lesson a title.').max(300),
    disciplineId: z.string().uuid().nullable().optional(),
    categorisationId: z.string().uuid().nullable().optional(),
    issueCategory: issueCategorySchema,
    impact: issueImpactSchema,
    /** When the form was filled in on the device — never when the server saw it. */
    capturedAt: z.string().datetime(),
    gpsLatitude: z.number().min(-90).max(90).nullable().optional(),
    gpsLongitude: z.number().min(-180).max(180).nullable().optional(),
    gpsAccuracyMetres: z.number().nonnegative().nullable().optional(),
    gpsCaptureStatus: gpsCaptureStatusSchema,
    description: narrative.optional(),
    rootCause: narrative.optional(),
    correction: narrative.optional(),
    correctiveAction: narrative.optional(),
    isRepeatedIssue: z.boolean().default(false),
    repeatCount: z.number().int().positive().nullable().optional(),
    repeatLocation: z.string().trim().max(500).nullable().optional(),
})
    .superRefine((value, ctx) => {
    // A "captured" status with no coordinates is a contradiction, and letting it
    // through would make the status field useless as evidence.
    if (value.gpsCaptureStatus === 'captured') {
        if (value.gpsLatitude === null || value.gpsLatitude === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['gpsLatitude'],
                message: 'A captured location needs coordinates.',
            });
        }
    }
    if (value.isRepeatedIssue && !value.repeatCount) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['repeatCount'],
            message: 'A repeated issue needs a repeat count.',
        });
    }
});
/** Identity, capture data and workflow state are fixed after creation. */
export const lessonUpdateSchema = z.object({
    title: z.string().trim().min(1).max(300).optional(),
    disciplineId: z.string().uuid().nullable().optional(),
    categorisationId: z.string().uuid().nullable().optional(),
    issueCategory: issueCategorySchema.optional(),
    impact: issueImpactSchema.optional(),
    description: narrative.optional(),
    rootCause: narrative.optional(),
    correction: narrative.optional(),
    correctiveAction: narrative.optional(),
    isRepeatedIssue: z.boolean().optional(),
    repeatCount: z.number().int().positive().nullable().optional(),
    repeatLocation: z.string().trim().max(500).nullable().optional(),
});
export const lessonSendBackSchema = z.object({
    remarks: z.string().trim().min(10, 'Explain what needs changing, in at least 10 characters.'),
});
export const photoAttachSchema = z.object({
    category: photoCategorySchema,
    fileId: z.string().uuid(),
    caption: z.string().trim().max(500).nullable().optional(),
    capturedAt: z.string().datetime().nullable().optional(),
});
// ---------------------------------------------------------------------------
// Photo annotation (pass 8; owner's decision: pen, arrow and text, with undo)
// ---------------------------------------------------------------------------
/**
 * Marks drawn over a photo. Stored beside it; the photo itself is never
 * altered, so the original stays evidence and the marks can be redrawn.
 *
 * Positions are fractions of the photo's width and height (0 to 1), so the
 * marks land in the same place at any size — on a phone, on a monitor and in
 * a PDF. A text mark's size is a fraction of the photo's width.
 */
export const ANNOTATION_COLOURS = {
    red: '#D92D20',
    yellow: '#FDB022',
    white: '#FFFFFF',
    black: '#101828',
};
const annotationPoint = z.tuple([z.number().min(0).max(1), z.number().min(0).max(1)]);
const annotationColour = z.enum(['red', 'yellow', 'white', 'black']);
export const photoAnnotationMarkSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('pen'), colour: annotationColour, points: z.array(annotationPoint).min(2).max(2000) }),
    z.object({ kind: z.literal('arrow'), colour: annotationColour, from: annotationPoint, to: annotationPoint }),
    z.object({
        kind: z.literal('text'),
        colour: annotationColour,
        at: annotationPoint,
        text: z.string().trim().min(1).max(200),
        size: z.number().min(0.01).max(0.2),
    }),
]);
/** The most points one photo's marks may hold, all strokes together. */
export const ANNOTATION_MAX_POINTS = 5000;
export const photoAnnotationSchema = z
    .object({ version: z.literal(1), marks: z.array(photoAnnotationMarkSchema).max(100) })
    .refine((value) => value.marks.reduce((total, mark) => total + (mark.kind === 'pen' ? mark.points.length : 2), 0) <= ANNOTATION_MAX_POINTS, { message: 'Those marks are too detailed to keep. Draw fewer or shorter strokes.', path: ['marks'] });
/** Set or clear a photo's marks. `null` clears them. */
export const photoAnnotateSchema = z.object({ annotation: photoAnnotationSchema.nullable() });
/** A stored annotation, or null if there is none or it cannot be read. */
export function parsePhotoAnnotation(stored) {
    if (!stored)
        return null;
    try {
        const parsed = photoAnnotationSchema.safeParse(JSON.parse(stored));
        return parsed.success ? parsed.data : null;
    }
    catch {
        return null;
    }
}
/** An arrow's head: two short strokes back from its tip, in the same fractions. */
export function arrowHead(from, to) {
    const angle = Math.atan2(to[1] - from[1], to[0] - from[0]);
    const length = Math.min(0.04, Math.hypot(to[0] - from[0], to[1] - from[1]) / 2);
    const wing = (offset) => [
        to[0] - length * Math.cos(angle - offset),
        to[1] - length * Math.sin(angle - offset),
    ];
    return [wing(Math.PI / 7), wing(-Math.PI / 7)];
}
// ---------------------------------------------------------------------------
// Offline sync
// ---------------------------------------------------------------------------
/**
 * Every field a person can edit on a lesson, as offline sync carries it
 * (DATA-3). Each travels as text; the server turns it back into its type and
 * checks it on arrival (`sync-fields.ts`).
 */
export const MERGEABLE_LESSON_FIELDS = [
    'title',
    'description',
    'rootCause',
    'correction',
    'correctiveAction',
    'repeatLocation',
    'disciplineId',
    'categorisationId',
    'issueCategory',
    'impact',
    'isRepeatedIssue',
    'repeatCount',
];
/**
 * One field's change, carrying the value the device started from.
 *
 * `base` is what makes a three-way merge possible. Without it the server can
 * only compare two values and guess which is newer — and guessing is how one
 * person's work gets silently overwritten, which spec §10 forbids outright.
 */
export const fieldChangeSchema = z.object({
    base: z.string().nullable(),
    value: z.string(),
});
export const syncRecordSchema = z.object({
    id: z.string().uuid(),
    /** Present when the device created this record while offline. */
    create: lessonCreateSchema.optional(),
    changes: z.record(z.enum(MERGEABLE_LESSON_FIELDS), fieldChangeSchema).optional(),
    clientUpdatedAt: z.string().datetime().optional(),
});
export const syncBatchSchema = z.object({
    // Capped so one device reconnecting after a long absence cannot send a payload
    // that times out mid-way and has to start again.
    records: z.array(syncRecordSchema).max(100),
});
export const conflictResolutionSchema = z
    .object({
    choice: z.enum(['server', 'incoming', 'manual']),
    value: z.string().max(10000).optional(),
})
    .superRefine((input, ctx) => {
    if (input.choice === 'manual' && !input.value?.trim()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['value'],
            message: 'Choosing to write your own version needs a value.',
        });
    }
});
/** Filters for the Lesson Learned Log (spec §10 key screens). */
export const lessonLogQuerySchema = z.object({
    projectId: z.string().uuid().optional(),
    issueCategory: issueCategorySchema.optional(),
    impact: issueImpactSchema.optional(),
    disciplineId: z.string().uuid().optional(),
    status: z.enum(['draft', 'submitted', 'sent_back', 'approved']).optional(),
    search: z.string().trim().max(200).optional(),
});
// ---------------------------------------------------------------------------
// Prompt-to-transaction (spec §13.3) — a sentence in, a DRAFT out
// ---------------------------------------------------------------------------
export const promptDraftRequestSchema = z.object({
    /** What the person typed. The only thing sent to the AI provider. */
    prompt: z.string().trim().min(10, 'Describe the lesson in a sentence or two.').max(2000),
    /**
     * The id the device already holds for the lesson it is about to create, so the
     * suggestion is tied to that lesson from the start. Nothing is created here.
     */
    lessonId: z.string().uuid(),
});
/** Fields the prompt can fill. Identity, capture data and GPS never come from a sentence. */
export const PROMPT_FIELDS = [
    'title',
    'projectId',
    'disciplineId',
    'issueCategory',
    'impact',
    'description',
    'rootCause',
    'correction',
    'correctiveAction',
];
export const promptDispositionSchema = z.object({
    /** Rejected: the draft was discarded, and no lesson was made from it. */
    claimed: z.enum(['accepted', 'edited', 'rejected']),
    /** Answers given to the clarifying questions, for the AI activity log. */
    answers: z.record(z.string(), z.string()).optional(),
});
//# sourceMappingURL=lesson-learned.js.map